import { APP_INITIALIZER, ErrorHandler, NgModule } from "@angular/core";
import { BrowserModule } from "@angular/platform-browser";
import { BrowserAnimationsModule } from "@angular/platform-browser/animations";
import { HTTP_INTERCEPTORS, HttpClientModule } from "@angular/common/http";
import { ClipboardModule } from "ngx-clipboard";
import { TranslateModule } from "@ngx-translate/core";
import { InlineSVGModule } from "ng-inline-svg-2";
import { NgbModule } from "@ng-bootstrap/ng-bootstrap";
import { AppRoutingModule } from "./app-routing.module";
import { AppComponent } from "./app.component";
import { AuthService } from "@modules/auth";
import { SplashScreenModule } from "./_metronic/partials/layout/splash-screen/splash-screen.module";
import { JwtInterceptor } from "./_helper/jwt-interceptor.interceptor";
import { ToastrModule } from "ngx-toastr";
import { FormsModule, ReactiveFormsModule } from "@angular/forms";
import { AvatarModule } from "ngx-avatars";
import { MatDialogModule } from "@angular/material/dialog";
import { MatButtonModule } from "@angular/material/button";
import { MatListModule } from "@angular/material/list";
import { SpinnerModule } from "./_metronic/shared/spinner/spinner.module";
import { createErrorHandler, TraceService } from "@sentry/angular";
import { Router, RouteReuseStrategy } from "@angular/router";
import { AppStoreModule } from "./app-store.module";
import { environment } from "src/environments/environment";
import { GoogleTagManagerModule } from "angular-google-tag-manager";
import { SharedModule } from "@shared/shared.module";
import { IonicModule, IonicRouteStrategy } from "@ionic/angular";
import { PushNotificationService } from "@services/PushNotificationService/PushNotification.service";
import { Capacitor } from "@capacitor/core";
import { firstValueFrom } from "rxjs";

function appInitializer(
  authService: AuthService,
  pushNotificationService: PushNotificationService
) {
  return async () => {
    await firstValueFrom(authService.getUserByToken()).catch((err) => {
      console.warn("[auth] Failed to restore session on app start:", err);
    });

    if (Capacitor.isNativePlatform()) {
      await pushNotificationService.requestPermission().catch((err) => {
        console.warn("[push] Failed to register on app start:", err);
      });
    }
  };
}

@NgModule({
  declarations: [AppComponent],
  imports: [
    BrowserAnimationsModule,
    BrowserModule,
    ToastrModule.forRoot({
      toastClass: "toastr-safe-padding ngx-toastr",
    }),
    SplashScreenModule,
    FormsModule,
    ReactiveFormsModule,
    TranslateModule.forRoot(),
    HttpClientModule,
    ClipboardModule,
    AvatarModule,
    InlineSVGModule.forRoot(),
    IonicModule.forRoot({
      menuType: "overlay",
      rippleEffect: false,
      mode: "ios",
    }),
    NgbModule,
    MatDialogModule,
    MatButtonModule,
    MatListModule,
    SpinnerModule,
    SharedModule,
    AppStoreModule,
    AppRoutingModule,
  ],
  providers: [
    IonicRouteStrategy,
    { provide: "googleTagManagerId", useValue: environment.gtmId },
    {
      provide: APP_INITIALIZER,
      useFactory: appInitializer,
      multi: true,
      deps: [AuthService, PushNotificationService],
    },
    { provide: HTTP_INTERCEPTORS, useClass: JwtInterceptor, multi: true },
    {
      provide: ErrorHandler,
      useValue: createErrorHandler({
        showDialog: false,
        logErrors: true,
      }),
    },
    {
      provide: TraceService,
      deps: [Router],
    },
    {
      provide: APP_INITIALIZER,
      useFactory: () => () => {},
      deps: [TraceService],
      multi: true,
    },
    { provide: RouteReuseStrategy, useClass: IonicRouteStrategy },
  ],
  bootstrap: [AppComponent],
})
export class AppModule {}
